import { ICompileService } from "angular";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../records/forms/formly/fieldConfigurationHelpers";
import {
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../records/forms/formView";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import { LookupManagementService } from "../../records/lookup/lookupManagement";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../records/views/recordGridView";
import fieldHelpers from "../../ui-formly/fieldHelpers";
import { vsLookupManagementServiceName as lookupServiceName } from "./lookups";
import { vsObjectName as mealLogReasonName } from "./reasons";
import { FilterProvider } from "../../records/grids/columns/filters/FilterProvider";
import { StringFilter } from "../../records/grids/columns/filters/subFilters/StringFilter";
import { RecordDataSource } from "../../records/grids";
import { ITemplateScope } from "AngularFormly";

export const vsObjectName = "mealLog";

mealLogConfig.$inject = [
  RecordGridViewServiceProviderName,
  FormViewServiceProviderName,
];
export function mealLogConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  configureGrid(recordGridViewServiceProvider);
  configureForm(formViewServiceProvider);
}
function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    GridSettingsServiceName,
    ColumnDefinitionServiceName,
    "$compile",
    (
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService,
      columnDefinitionService: ColumnDefinitionService,
      $compile: ICompileService
    ) => {
      return {
        columns: [
          columnDefinitionService.getStringColDef({
            field: "SerialNumber",
            header: "Serial Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "SlotNumber",
            header: "Slot Number",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Location",
          }),
          columnDefinitionService.getStringColDef({
            field: "MealLogReasonName",
            header: "Reason",
          }),
          columnDefinitionService.getStringColDef({
            field: "CreatedByName",
            header: "Employee",
            suppressSorting: true,
            filter: new FilterProvider("CreatedByName", $compile)
              .addFilter(
                "contains",
                new StringFilter({
                  displayName: "contains",
                  queryFieldNames: ["EmployeeNameContains"],
                })
              )
              .addFilter(
                "=",
                new StringFilter({
                  displayName: "=",
                  queryFieldNames: ["EmployeeName"],
                })
              )
              .addFilter(
                "<=",
                new StringFilter({
                  displayName: "<=",
                  queryFieldNames: ["EmployeeNameLessThanOrEqualTo"],
                })
              )
              .addFilter(
                ">=",
                new StringFilter({
                  displayName: ">=",
                  queryFieldNames: ["EmployeeNameGreaterThanOrEqualTo"],
                })
              )
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getStringColDef({
            field: "Notes",
          }),
          columnDefinitionService.getDateTimeColDef({
            field: "OpenedDate",
            header: "Opened",
            filter: new FilterProvider("OpenedDate", $compile)
              .defaultDateTimeFilters()
              .excludeFilters(["="])
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getDateTimeColDef({
            field: "ClosedDate",
            header: "Closed",
            filter: new FilterProvider("ClosedDate", $compile)
              .defaultDateTimeFilters()
              .excludeFilters(["="])
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getDateTimeColDef({
            field: "CreatedDate",
            header: "Created Date",
            hide: true,
            suppressMenu: true,
          }),
        ],
        defaultSort: "-CreatedDate",
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/machines/mealLog"
        ),
        gridSettings: gridSettingsService.getGridSettings(Symbol(vsObjectName)),
        namePlural: "MEAL Logs",
        nameSingular: "MEAL Log",
        showAddButton: true,
      };
    },
  ]);
}

function configureForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FormViewServiceName,
    FieldConfigurationHelperServiceName,
    lookupServiceName,
    (
      formViewService: FormViewService,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      lookupService: LookupManagementService
    ) => {
      return {
        fields: [
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(lookupService, {
                key: "MealLogReasonId",
                objectName: mealLogReasonName,
                required: true,
              }),
              fieldHelpers.simpleDateTimeField({
                key: "OpenedDate",
                label: "Opened",
                required: true,
              }),
              fieldHelpers.simpleDateTimeField({
                key: "ClosedDate",
                label: "Closed",
                required: true,
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.textAreaField({
                key: "Notes",
                required: false,
                rows: 5,
                expressionProperties: {
                  "templateOptions.required": (
                    $viewValue: any,
                    $modelValue: any,
                    scope: ITemplateScope
                  ) => {
                    const reason = scope.model?.MealLogReason; //this is the selected MealLogReason
                    return reason?.NotesRequired ?? false; //this field needs to be added to MealLogReason on server side
                  },
                },
              }),
            ],
            1
          ),
        ],
        name: "MEAL Log",
        submitForm: (args) =>
          formViewService.saveForm({
            formObject: args.model,
            formViewController: args.formViewController,
            url: "/api/machines/mealLog",
          }),
        title: (model) => (model.Id ? "Edit" : "Create") + " MEAL Log",
      };
    },
  ]);
}
